export interface ChestItemConfig {
    weight: number;
    enabled: boolean;
}

export interface ChestContextConfig {
    enabled: boolean;
    items: Record<string, ChestItemConfig>;
}

export interface ChestRewardConfig {
    version: number;
    contexts: Record<string, ChestContextConfig>;
}

export interface EquipmentCatalogItem {
    id: string;
    name: string;
    tier: string;
    category: string;
}

export interface ChestConfigResponse {
    config: ChestRewardConfig;
    merch: MerchControlData;
    equipmentCatalog: EquipmentCatalogItem[];
}

export interface MerchControlData {
    weights: Record<string, number>;
    quotas: Array<{
        subtype: string;
        budget_units: number;
        granted_units: number;
        period_week: string;
    }>;
    period_week: string;
    super_treasure_weekly?: {
        budget_units?: number;
        granted_units?: number;
        period_week?: string;
    };
}

export type ChestContextKey =
    | 'capture'
    | 'battle'
    | 'transmute'
    | 'epesca_chest'
    | 'epesca_battle'
    | 'quiz'
    | 'epesca_encounters';

export interface ItemMeta {
    key: string;
    label: string;
    description?: string;
    group: string;
}

export const CAPTURE_ITEMS: ItemMeta[] = [
    { key: 'rusty_hook', label: 'Anzuelo oxidado', group: 'Objetos de captura' },
    { key: 'fish_scale', label: 'Escama', group: 'Objetos de captura' },
    { key: 'stone', label: 'Piedra', group: 'Objetos de captura' },
    { key: 'boot', label: 'Bota vieja', group: 'Objetos de captura' },
    { key: 'squid_tentacle', label: 'Tentáculo', group: 'Objetos de captura' },
    { key: 'jellyfish', label: 'Medusa', group: 'Objetos de captura' },
    { key: 'algae', label: 'Algas', group: 'Objetos de captura' },
    { key: 'starfish', label: 'Estrella de mar', group: 'Objetos de captura' },
    { key: 'crab', label: 'Cangrejo', group: 'Objetos de captura' },
    { key: 'big_fish_bite', label: 'Picada increíble', group: 'Objetos de captura' },
    { key: 'fisherman_touch', label: 'Toque Pescador', group: 'Objetos de captura' },
    { key: 'extra_faith', label: 'Dosis extra de fe', group: 'Objetos de captura' },
    { key: 'photo_art', label: 'Arte para fotos', group: 'Objetos de captura' },
    { key: 'magic_fishing_spot', label: 'Sitio mágico de pesca', group: 'Objetos de captura' },
    { key: 'message_bottle', label: 'Botella con mensaje', group: 'Objetos de captura' },
];

/** La botella con mensaje es coleccionable también en batalla (contexto `battle`). */
export const MESSAGE_BOTTLE_ITEM: ItemMeta = CAPTURE_ITEMS[CAPTURE_ITEMS.length - 1];

export const BATTLE_ITEMS: ItemMeta[] = [
    { key: 'splintered_spine', label: 'Espina astillada', group: 'Objetos de batalla' },
    { key: 'anisaquis_entrails', label: 'Anisaquis', group: 'Objetos de batalla' },
    { key: 'scale_between_teeth', label: 'Escama entre dientes', group: 'Objetos de batalla' },
    { key: 'all_seeing_fish_eye', label: 'Ojo de pez', group: 'Objetos de batalla' },
    { key: 'glory_victory_echo', label: 'Eco de gloria', group: 'Objetos de batalla' },
    { key: 'unfortunate_fish_bone', label: 'Raspa desafortunada', group: 'Objetos de batalla' },
    { key: 'torn_shark_fin', label: 'Aleta de tiburón', group: 'Objetos de batalla' },
    { key: 'chipped_tooth', label: 'Diente mellado', group: 'Objetos de batalla' },
    { key: 'honor_violence', label: 'Honor y violencia', group: 'Objetos de batalla' },
];

export const JOKER_ITEMS: ItemMeta[] = [
    { key: 'joker_fifty_fifty', label: 'Comodín 50/50', group: 'Comodines Quiz' },
    { key: 'joker_audience', label: 'Comodín Público', group: 'Comodines Quiz' },
    { key: 'joker_call', label: 'Comodín Llamada', group: 'Comodines Quiz' },
];

export const PRO_1M_ADMIN_NOTE =
    'Sale a cualquier usuario. Quien lo gana solo puede regalarlo; quien lo recibe puede activarlo.';

export const PRO_CAPTURE: ItemMeta[] = [
    {
        key: 'pro_1m',
        label: 'Pro 1 mes',
        group: 'Suscripción Pro',
        description: 'Para regalar. Sale a Pro y a no Pro',
    },
];

export const PRO_TRANSMUTE: ItemMeta[] = [
    {
        key: 'pro_1m',
        label: 'Pro 1 mes',
        group: 'Suscripción Pro',
        description: 'Para regalar. Sale a Pro y a no Pro',
    },
    { key: 'pro_3m', label: 'Pro 3 meses', group: 'Suscripción Pro' },
    { key: 'pro_6m', label: 'Pro 6 meses', group: 'Suscripción Pro' },
    { key: 'pro_1y', label: 'Pro 1 año', group: 'Suscripción Pro' },
    { key: 'pro_forever', label: 'Pro para siempre', group: 'Suscripción Pro' },
];

export const MERCH_ITEMS: ItemMeta[] = [
    {
        key: 'merch_camiseta_corta',
        label: 'Camiseta manga corta',
        group: 'Merchandising',
        description: 'Premio físico — solo usuarios elegibles (ver nota del grupo)',
    },
    {
        key: 'merch_gorra',
        label: 'Gorra',
        group: 'Merchandising',
        description: 'Premio físico — solo usuarios elegibles (ver nota del grupo)',
    },
    {
        key: 'merch_camiseta_larga',
        label: 'Camiseta manga larga',
        group: 'Merchandising',
        description: 'Premio físico — solo usuarios elegibles (ver nota del grupo)',
    },
    {
        key: 'merch_sudadera',
        label: 'Sudadera',
        group: 'Merchandising',
        description: 'Premio físico — solo usuarios elegibles (ver nota del grupo)',
    },
];

/** Texto canónico para admin: elegibilidad merch en runtime */
export const MERCH_GEO_ADMIN_NOTE =
    'Solo se entrega a usuarios con país ES, ubicación en perfil (país + región o coordenadas) y zona peninsular. ' +
    'Quedan excluidos Baleares, Canarias, Ceuta y Melilla. Si el usuario no es elegible, la tirada hace fallback a otra recompensa.';

export const MERCH_DEFAULT_SHARES: Record<string, number> = {
    merch_camiseta_corta: 2.4,
    merch_gorra: 1.8,
    merch_camiseta_larga: 1.2,
    merch_sudadera: 0.6,
};

/** @deprecated Usar MERCH_ITEMS */
export const MERCH_SLOT: ItemMeta[] = MERCH_ITEMS;

export const EQUIPMENT_CATEGORY_LABELS: Record<string, string> = {
    rod: 'Cañas',
    reel: 'Carretes',
    lure: 'Señuelos',
    bait: 'Cebo',
    vessel: 'Embarcaciones',
    companion: 'Compañeros',
};

const TIER_LABELS: Record<string, string> = {
    basico: 'Básico',
    intermedio: 'Intermedio',
    avanzado: 'Avanzado',
};

export function isEquipmentCatalogKey(key: string, catalog: EquipmentCatalogItem[]): boolean {
    return catalog.some((c) => c.id === key);
}

export function contextIncludesCompanionEquipment(context: ChestContextKey): boolean {
    return context === 'epesca_chest' || context === 'epesca_battle';
}

/** El equipo de colección ya no sale de cofres (solo craft / capítulos / loadout). */
export function isChestBlockedEquipmentCategory(_category: string): boolean {
    return true;
}

export function isChestEligibleEquipment(
    _item: Pick<EquipmentCatalogItem, 'category'>,
    _includeCompanions: boolean
): boolean {
    return false;
}

function isEpescaRewardContext(context: ChestContextKey): boolean {
    return context === 'epesca_chest' || context === 'epesca_battle';
}

function contextUsesEquipmentPool(_context: ChestContextKey): boolean {
    return false;
}

const EQUIPMENT_KEY_PREFIX = /^(rod|reel|lure|bait|vessel|companion)_/;

function stripBlockedEquipmentKeys(
    items: Record<string, ChestItemConfig>,
    catalog: EquipmentCatalogItem[]
): Record<string, ChestItemConfig> {
    const blockedIds = new Set(catalog.map((c) => c.id));
    const next = { ...items };
    for (const key of Object.keys(next)) {
        if (key === 'equipment' || EQUIPMENT_KEY_PREFIX.test(key) || blockedIds.has(key)) {
            delete next[key];
        }
    }
    return next;
}

function normalizePercentWeights(items: Record<string, ChestItemConfig>): Record<string, ChestItemConfig> {
    const activeKeys = Object.keys(items).filter((key) => items[key].enabled && Number(items[key].weight) > 0);
    const total = activeKeys.reduce((sum, key) => sum + Number(items[key].weight), 0);
    if (total <= 0 || Math.abs(total - 100) < 0.005) return items;

    const normalized = { ...items };
    let assigned = 0;
    activeKeys.forEach((key, index) => {
        const weight =
            index === activeKeys.length - 1
                ? Math.round((100 - assigned) * 100) / 100
                : Math.round((Number(items[key].weight) / total) * 10000) / 100;
        assigned += weight;
        normalized[key] = { ...items[key], weight };
    });
    return normalized;
}

export function equipmentTierWeight(tier: string): number {
    const t = tier.toLowerCase();
    if (t === 'basico') return 4;
    if (t === 'intermedio') return 2;
    if (t === 'avanzado') return 1;
    return 1;
}

export function buildEquipmentItems(
    catalog: EquipmentCatalogItem[],
    includeCompanions: boolean
): ItemMeta[] {
    return catalog
        .filter((c) => isChestEligibleEquipment(c, includeCompanions))
        .map((c) => ({
            key: c.id,
            label: c.name,
            description: `Tier ${TIER_LABELS[c.tier.toLowerCase()] ?? c.tier}`,
            group: `Equipamiento — ${EQUIPMENT_CATEGORY_LABELS[c.category] ?? c.category}`,
        }));
}

export function defaultEquipmentTotal(_context: ChestContextKey): number {
    return 0;
}

export function splitEquipmentTotalInItems(
    items: Record<string, ChestItemConfig>,
    catalog: EquipmentCatalogItem[],
    includeCompanions: boolean
): Record<string, ChestItemConfig> {
    const legacy = items.equipment;
    const next = { ...items };
    delete next.equipment;

    if (!legacy) return next;

    const hasIndividual = catalog.some((c) => {
        if (!isChestEligibleEquipment(c, includeCompanions)) return false;
        const row = items[c.id];
        return row && parseItemEnabled(row.enabled) && Number(row.weight) > 0;
    });
    if (hasIndividual) return next;

    const eligible = catalog.filter((c) => isChestEligibleEquipment(c, includeCompanions));
    const total = Number(legacy.weight) || 0;
    const enabled = parseItemEnabled(legacy.enabled);
    const tierSum = eligible.reduce((s, c) => s + equipmentTierWeight(c.tier), 0);
    if (tierSum <= 0 || eligible.length === 0) return next;

    let assigned = 0;
    eligible.forEach((c, i) => {
        const weight =
            i === eligible.length - 1
                ? Math.round((total - assigned) * 100) / 100
                : Math.round((total * equipmentTierWeight(c.tier)) / tierSum * 100) / 100;
        assigned += weight;
        next[c.id] = { weight, enabled };
    });
    return next;
}

function applyEquipmentDefaults(
    items: Record<string, ChestItemConfig>,
    catalog: EquipmentCatalogItem[],
    total: number,
    includeCompanions: boolean
): Record<string, ChestItemConfig> {
    const eligible = catalog.filter((c) => isChestEligibleEquipment(c, includeCompanions));
    const tierSum = eligible.reduce((s, c) => s + equipmentTierWeight(c.tier), 0);
    if (tierSum <= 0 || eligible.length === 0) return items;

    let assigned = 0;
    eligible.forEach((c, i) => {
        const weight =
            i === eligible.length - 1
                ? Math.round((total - assigned) * 100) / 100
                : Math.round((total * equipmentTierWeight(c.tier)) / tierSum * 100) / 100;
        assigned += weight;
        // Solo rellenar keys ausentes. Nunca reactivar un ítem desactivado a propósito.
        if (!items[c.id]) {
            items[c.id] = { weight, enabled: true };
        }
    });
    return items;
}

export const EPESCA_ENCOUNTER_ITEMS: ItemMeta[] = [
    { key: 'encounter_chest', label: 'Cofre', description: 'Encuentro con cofre (acertijo)', group: 'Tipo de encuentro' },
    { key: 'encounter_battle', label: 'Batalla', description: 'Encuentro PvP en patrulla', group: 'Tipo de encuentro' },
    { key: 'encounter_cromo', label: 'Cromo de especie', group: 'Tipo de encuentro' },
    { key: 'encounter_creature', label: 'Criatura', group: 'Tipo de encuentro' },
];

export const CONTEXT_TABS: Array<{
    key: ChestContextKey;
    label: string;
    description: string;
    percentMode: boolean;
}> = [
    {
        key: 'capture',
        label: 'Captura',
        description: 'Cofre al registrar una captura',
        percentMode: true,
    },
    {
        key: 'battle',
        label: 'Batalla PvP',
        description: 'Cofre para el ganador de una batalla',
        percentMode: true,
    },
    {
        key: 'epesca_encounters',
        label: 'e-Pesca — Encuentros',
        description: 'Probabilidad relativa de cada tipo de resultado al patrullar (antes de multiplicadores de loadout)',
        percentMode: false,
    },
    {
        key: 'epesca_chest',
        label: 'e-Pesca — Cofre',
        description: 'Contenido del cofre cuando la patrulla cae en encuentro cofre',
        percentMode: true,
    },
    {
        key: 'epesca_battle',
        label: 'e-Pesca — Batalla',
        description: 'Recompensa al ganar una batalla en patrulla',
        percentMode: true,
    },
    {
        key: 'quiz',
        label: 'Quiz',
        description: 'Recompensa al completar quiz con 5+ aciertos',
        percentMode: true,
    },
];

export function splitMerchTotalInItems(
    items: Record<string, ChestItemConfig>
): Record<string, ChestItemConfig> {
    const legacy = items.merch_total;
    const next = { ...items };
    delete next.merch_total;

    if (!legacy) return next;

    const hasIndividualMerch = MERCH_ITEMS.some((m) => {
        const row = items[m.key];
        return row && (row.enabled !== false) && Number(row.weight) > 0;
    });
    if (hasIndividualMerch) return next;

    const total = Number(legacy.weight) || 0;
    const enabled = parseItemEnabled(legacy.enabled);
    const shares = [0.4, 0.3, 0.2, 0.1];
    const keys = MERCH_ITEMS.map((m) => m.key);

    let assigned = 0;
    keys.forEach((key, i) => {
        const weight =
            i === keys.length - 1
                ? Math.round((total - assigned) * 100) / 100
                : Math.round(total * shares[i] * 100) / 100;
        assigned += weight;
        next[key] = { weight, enabled };
    });
    return next;
}

export function parseItemEnabled(value: unknown): boolean {
    if (value === false || value === 'false' || value === 0 || value === '0') return false;
    return true;
}

export function defaultMerchWeight(key: string): number {
    return MERCH_DEFAULT_SHARES[key] ?? 1;
}

export function getItemsForContext(
    context: ChestContextKey,
    _catalog: EquipmentCatalogItem[] = []
): ItemMeta[] {
    switch (context) {
        case 'capture':
            return [...PRO_CAPTURE, ...JOKER_ITEMS, ...CAPTURE_ITEMS];
        case 'battle':
            return [...PRO_CAPTURE, ...JOKER_ITEMS, ...BATTLE_ITEMS, MESSAGE_BOTTLE_ITEM];
        case 'epesca_encounters':
            return EPESCA_ENCOUNTER_ITEMS;
        case 'transmute':
        case 'quiz':
            return [
                ...PRO_TRANSMUTE,
                ...JOKER_ITEMS,
                ...BATTLE_ITEMS,
                ...CAPTURE_ITEMS,
            ];
        case 'epesca_chest':
        case 'epesca_battle':
            return [
                ...PRO_TRANSMUTE,
                ...JOKER_ITEMS,
            ];
        default:
            return [];
    }
}

export function buildDefaultContext(
    context: ChestContextKey,
    catalog: EquipmentCatalogItem[] = []
): ChestContextConfig {
    const items: Record<string, ChestItemConfig> = {};
    for (const meta of getItemsForContext(context, catalog)) {
        if (context === 'epesca_encounters') {
            items[meta.key] = {
                weight: meta.key === 'encounter_chest' || meta.key === 'encounter_battle' ? 25 : 15,
                enabled: true,
            };
        } else if (context === 'capture') {
            if (CAPTURE_ITEMS.some((i) => i.key === meta.key)) {
                items[meta.key] = { weight: meta.key === 'rusty_hook' ? 5 : 4, enabled: true };
            } else if (meta.key === 'pro_1m') items[meta.key] = { weight: 2, enabled: true };
            else if (MERCH_ITEMS.some((i) => i.key === meta.key)) {
                /* se rellena abajo */
            } else if (isEquipmentCatalogKey(meta.key, catalog)) {
                /* se rellena abajo */
            } else if (meta.key.startsWith('joker_')) items[meta.key] = { weight: 10, enabled: true };
            else items[meta.key] = { weight: 1, enabled: true };
        } else if (context === 'battle') {
            if (meta.key === 'pro_1m') items[meta.key] = { weight: 2, enabled: true };
            else if (MERCH_ITEMS.some((i) => i.key === meta.key)) {
                /* se rellena abajo */
            } else if (meta.key.startsWith('joker_')) {
                items[meta.key] = { weight: meta.key === 'joker_call' ? 15.34 : 15.33, enabled: true };
            } else if (
                BATTLE_ITEMS.some((i) => i.key === meta.key) ||
                meta.key === MESSAGE_BOTTLE_ITEM.key
            ) {
                items[meta.key] = { weight: 5.11, enabled: true };
            } else {
                items[meta.key] = { weight: 1, enabled: true };
            }
        } else {
            if (meta.key === 'pro_1m') items[meta.key] = { weight: 3, enabled: true };
            else if (meta.key === 'pro_3m') items[meta.key] = { weight: 1.5, enabled: true };
            else if (meta.key === 'pro_6m') items[meta.key] = { weight: 0.8, enabled: true };
            else if (meta.key === 'pro_1y') items[meta.key] = { weight: 0.5, enabled: true };
            else if (meta.key === 'pro_forever') items[meta.key] = { weight: 0.2, enabled: true };
            else if (MERCH_ITEMS.some((i) => i.key === meta.key)) {
                /* se rellena abajo */
            } else if (isEquipmentCatalogKey(meta.key, catalog)) {
                /* se rellena abajo */
            } else if (meta.key.startsWith('joker_')) {
                items[meta.key] = {
                    weight: meta.key === 'joker_call' ? 14.26 : 14.25,
                    enabled: true,
                };
            } else if (BATTLE_ITEMS.some((i) => i.key === meta.key)) {
                items[meta.key] = { weight: 2.38, enabled: true };
            } else if (CAPTURE_ITEMS.some((i) => i.key === meta.key)) {
                items[meta.key] = { weight: 1.53, enabled: true };
            } else {
                items[meta.key] = { weight: 1, enabled: true };
            }
        }
    }
    if (context !== 'epesca_encounters') {
        if (catalog.length > 0 && contextUsesEquipmentPool(context)) {
            applyEquipmentDefaults(
                items,
                catalog,
                defaultEquipmentTotal(context),
                contextIncludesCompanionEquipment(context)
            );
        }
    }
    const cleaned = stripBlockedEquipmentKeys(items, catalog);
    return {
        enabled: true,
        items: isEpescaRewardContext(context) ? normalizePercentWeights(cleaned) : cleaned,
    };
}

export function normalizeContextConfig(
    context: ChestContextKey,
    raw?: ChestContextConfig | null,
    catalog: EquipmentCatalogItem[] = []
): ChestContextConfig {
    const defaults = buildDefaultContext(context, catalog);
    if (!raw) return defaults;
    const items: Record<string, ChestItemConfig> = { ...defaults.items };
    for (const meta of getItemsForContext(context, catalog)) {
        const saved = raw.items?.[meta.key];
        if (saved) {
            items[meta.key] = {
                weight: Number(saved.weight) || 0,
                enabled: parseItemEnabled(saved.enabled),
            };
        }
    }
    const includeCompanions = contextIncludesCompanionEquipment(context);
    let withEquipment = splitEquipmentTotalInItems(items, catalog, includeCompanions);
    if (catalog.length > 0 && contextUsesEquipmentPool(context)) {
        applyEquipmentDefaults(
            withEquipment,
            catalog,
            defaultEquipmentTotal(context),
            includeCompanions
        );
    }
    withEquipment = stripBlockedEquipmentKeys(withEquipment, catalog);
    const normalized = {
        enabled: raw.enabled !== false,
        items: withEquipment,
    };
    // Renormalizar a % en contextos percentMode para compensar equipo eliminado.
    const tab = CONTEXT_TABS.find((t) => t.key === context);
    return tab?.percentMode
        ? { ...normalized, items: normalizePercentWeights(normalized.items) }
        : normalized;
}
